"use client";

/**
 * SlideDeckPreview — Pitch/Keynote-style deck preview (Wave, May 2026).
 *
 * Large current-slide "stage" + a clickable vertical thumbnail rail. Fed
 * by BE-rendered slide PNG `data:` URIs (the host fetches them through the
 * authenticated apiClient and hands them down — this component is purely
 * presentational, no network).
 *
 * Loading + error + empty states are first-class so the host can hand us a
 * pending/failed fetch without branching its own JSX.
 */

import { ChevronLeft, ChevronRight, ImageOff, RotateCw } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";
import { Button } from "../primitives/button";

export interface SlideDeckPreviewProps {
  /** Ordered slide PNG sources (`data:image/png;base64,...`). */
  readonly thumbnails: ReadonlyArray<string>;
  /** Total slide count (may exceed thumbnails.length while rendering). */
  readonly slideCount: number;
  /** True while the preview fetch is in flight. */
  readonly loading: boolean;
  /** Non-null when the last fetch failed. */
  readonly error: string | null;
  /** Retry callback for the error state. */
  readonly onRetry: () => void;
  /**
   * Optional — reserved for a future per-slide AI affordance on the
   * preview. Currently unused here (the per-slide AI bar lives in the
   * structured ``SlideOutlineEditor``); kept optional so existing callers
   * compile unchanged.
   */
  readonly onAiEditSlide?: (index: number, instruction: string) => void;
}

export const SlideDeckPreview: React.FC<SlideDeckPreviewProps> = ({
  thumbnails,
  slideCount,
  loading,
  error,
  onRetry,
}) => {
  const [active, setActive] = React.useState(0);

  // Clamp the active index when the deck shrinks (e.g. slides removed in
  // the editor between two preview fetches).
  React.useEffect(() => {
    setActive((cur) => {
      if (thumbnails.length === 0) return 0;
      return Math.min(cur, thumbnails.length - 1);
    });
  }, [thumbnails.length]);

  const atStart = active <= 0;
  const atEnd = active >= thumbnails.length - 1;

  const goPrev = React.useCallback(() => {
    setActive((cur) => Math.max(0, cur - 1));
  }, []);
  const goNext = React.useCallback(() => {
    setActive((cur) => Math.min(thumbnails.length - 1, cur + 1));
  }, [thumbnails.length]);

  // Keyboard nav when the stage is focused.
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") goPrev();
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
    },
    [goPrev, goNext],
  );

  if (loading && thumbnails.length === 0) {
    return <SlideDeckPreviewSkeleton />;
  }

  if (error && thumbnails.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <ImageOff className="h-8 w-8 text-ink-3" aria-hidden />
        <p className="max-w-sm text-ink-2 text-sm">{error}</p>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Riprova
        </Button>
      </div>
    );
  }

  if (thumbnails.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <ImageOff className="h-7 w-7 text-ink-3" aria-hidden />
        <p className="text-ink-3 text-sm">Nessuna anteprima disponibile</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/* Thumbnail rail */}
      <div className="h-full w-[164px] shrink-0 overflow-y-auto border-hairline border-r bg-cream/40 p-2">
        <div className="flex flex-col gap-2">
          {thumbnails.map((src, i) => (
            <button
              type="button"
              key={`${i}-${src.slice(0, 24)}`}
              onClick={() => setActive(i)}
              aria-label={`Vai alla slide ${i + 1}`}
              aria-current={i === active}
              className={cn(
                "group relative overflow-hidden rounded-lg border-2 transition-colors",
                i === active
                  ? "border-ando-orange"
                  : "border-hairline hover:border-ando-orange/50",
              )}
            >
              <img
                src={src}
                alt={`Slide ${i + 1}`}
                className="block aspect-video w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Stage */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="relative flex min-h-0 flex-1 items-center justify-center bg-cream/30 p-4"
          role="button"
          tabIndex={0}
          aria-label="Anteprima slide corrente"
          onKeyDown={onKeyDown}
        >
          <img
            src={thumbnails[active]}
            alt={`Slide ${active + 1}`}
            className="max-h-full max-w-full rounded-lg border border-hairline object-contain shadow-sm"
          />
        </div>
        <div className="flex items-center justify-center gap-3 border-hairline border-t bg-cream/40 px-4 py-2">
          <button
            type="button"
            onClick={goPrev}
            disabled={atStart}
            aria-label="Slide precedente"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-2 transition-colors hover:bg-cream-deep/50 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <span className="text-ink-3 text-xs tabular-nums">
            {active + 1} / {Math.max(thumbnails.length, slideCount)}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={atEnd}
            aria-label="Slide successiva"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-2 transition-colors hover:bg-cream-deep/50 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
};

const SlideDeckPreviewSkeleton: React.FC = () => (
  <div className="flex h-full min-h-0">
    <div className="h-full w-[164px] shrink-0 border-hairline border-r bg-cream/40 p-2">
      <div className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="aspect-video w-full animate-pulse rounded-lg bg-cream-deep/40"
          />
        ))}
      </div>
    </div>
    <div className="flex min-w-0 flex-1 items-center justify-center p-4">
      <div className="aspect-video w-full max-w-3xl animate-pulse rounded-lg bg-cream-deep/40" />
    </div>
  </div>
);
SlideDeckPreviewSkeleton.displayName = "SlideDeckPreviewSkeleton";

SlideDeckPreview.displayName = "SlideDeckPreview";
